import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import React, { useEffect, useState } from 'react';
import QRCode from 'react-qr-code';

import {
  CreditYellowUserIcon,
  IconCopy,
  IconCopyRefer,
  IconHashTag,
  IconKey,
  IconLinkArtwork,
  IconLinkCollection,
  IconLogout,
  IconVerify16,
  ImageBgBuyCredit,
} from '@/assets';
import AvatarPlaceholder from '@/components/avatar-default';
import PopoverCustom from '@/components/popover-custom';
import { configs } from '@/configs';
import { ProfileTabKey } from '@/contents/user-profile/content';
import { useRouter } from '@/provider/RouterProvider';
import { useAuth } from '@/provider/UserProvider';
import { getPlan } from '@/services/pricing';
import { checkAppliedReferral, setCodeReferral } from '@/services/provider';
import { centerTextEllipsis, copyClipboard } from '@/utils';
import useUserInfo from './useUserInfo';
import { getMarketplaceURL } from '../sidebar/helpers';
import Image from 'next/image';

const BASE_URL = configs.ssoCallback || 'https://airight.io';

const linkIcons = {
  [ProfileTabKey.artwork]: <IconLinkArtwork />,
  [ProfileTabKey.collection]: <IconLinkCollection />,
  [ProfileTabKey.processing]: <IconLinkCollection />,
  [ProfileTabKey.template]: <IconHashTag />,
  [ProfileTabKey.bookmarks]: <IconHashTag />,
  [ProfileTabKey.model]: <IconHashTag />,
  [ProfileTabKey.apikey]: <IconKey />,
};

const UserInfo: React.FC<{
  logout: () => void;
  isHotStyle?: boolean;
}> = ({ logout, isHotStyle }) => {
  const searchParams = useSearchParams();
  const { auth, setAuth, isLogin } = useAuth();
  const { user, accessToken } = auth;
  const router = useRouter();
  const { listLink } = useUserInfo();
  const refBy = searchParams.get('refBy');
  const [isAppliedReferral, setIsAppliedReferral] = useState(false);
  const [active, setActive] = useState(false);

  const handleGetCredit = async () => {
    try {
      const res = await getPlan();

      if (res && res.credit !== undefined) {
        setAuth({ ...auth, user: { ...auth.user, credit: res?.credit } });
      } else {
        console.error('Invalid response format from getPlan');
      }
    } catch (error) {
      console.error('Error fetching credit');
    }
  };

  const checkReferral = async () => {
    const result: any = await checkAppliedReferral(auth.user.publicAddress);
    setIsAppliedReferral(result.data.code === null);
  };

  const setReferral = async () => {
    try {
      await setCodeReferral(refBy);
    } catch (error) {
      setIsAppliedReferral(false);
    }
  };

  useEffect(() => {
    if (auth?.accessToken) checkReferral();
  }, [auth.user.refCode]);

  useEffect(() => {
    if (refBy && isAppliedReferral) setReferral();
  }, [refBy, isAppliedReferral]);

  useEffect(() => {
    if (accessToken && isLogin) {
      handleGetCredit();
    }
  }, [accessToken, isLogin]);

  const triggerElement = (
    <div className="flex cursor-pointer items-center">
      <div className="relative h-[44px] w-[44px] rounded-[50%] md:h-10 md:w-10">
        <AvatarPlaceholder
          src={user.picture}
          address={user.publicAddress}
          className="h-full w-full rounded-[50%] object-cover"
          decor={user.avatarDecoration}
          decorScale={23}
        />
      </div>
      <div className="ml-2 flex w-[calc(100%-52px)] flex-col text-start lg:hidden">
        <div
          aria-selected={isHotStyle}
          className="flex items-center truncate text-[14px] font-[600] aria-selected:text-white"
        >
          <span className="w-[60px] truncate">{user?.username}</span>
          {user.sVerify === 'success' && (
            <span className="">
              <IconVerify16 />
            </span>
          )}
        </div>
        <span
          aria-expanded={auth.user.isPlus}
          aria-selected={isHotStyle}
          className="text-[12px] text-[#777e90] aria-expanded:w-fit aria-expanded:rounded-[36px] aria-expanded:!text-orange-500 aria-selected:text-white"
        >
          <div>
            <span className="text-12 font-normal leading-4">{auth.user?.credit}</span>
            <span className="text-12 font-normal leading-4"> credits</span>
          </div>
        </span>
      </div>
    </div>
  );

  const contentElement = (
    <div className="w-[320px] rounded-[16px] bg-white p-5 shadow-xl">
      <div
        className="flex items-center"
        onClick={() => router.push(`/profile/${user.publicAddress}`)}
      >
        <div className="relative h-[60px] w-[60px] cursor-pointer">
          <AvatarPlaceholder
            src={user.picture}
            address={user.publicAddress}
            className="h-full w-full rounded-[50%] object-cover"
            decorScale={33}
            decor={user.avatarDecoration}
          />
        </div>
        <div className="ml-4 flex flex-col">
          <div className="mb-1 flex cursor-pointer items-center gap-[2px] text-[14px] font-[600]">
            <p className="line-clamp-1 max-w-[120px]"> {user?.username || 'Unname'}</p>
            {user.sVerify === 'success' && (
              <span>
                <IconVerify16 />
              </span>
            )}
          </div>
          <div className="flex items-center">
            <span className="cursor-pointer text-[14px] text-gray-500">
              {centerTextEllipsis(user.publicAddress, 8)}
            </span>
            <IconCopy
              onClick={(e: any) => copyClipboard(e, user.publicAddress)}
              className="ml-2 cursor-pointer"
            />
          </div>
        </div>
      </div>
      <Link
        target="_blank"
        href={getMarketplaceURL(auth.isOwallet || false, '/buy-credit')}
        style={{ background: 'linear-gradient(85deg, #FFE5DD 2.51%, #FFBAA3 106.89%)' }}
        className="flex rounded-[20px] px-4 py-3 gap-[12px] mt-4 relative overflow-hidden"
      >
        <Image className="absolute left-0 top-0 w-full h-full" alt="bg" src={ImageBgBuyCredit} />
        <div className="w-[32px] h-[32px] relative rounded-full bg-[#FEF2D3] flex justify-center items-center">
          <CreditYellowUserIcon className="scale-75" />
        </div>
        <div className="text-left w-[calc(100%-44px)] relative">
          <p className="text-[13px] font-semibold">Buy Credits with AIRI</p>
          <p className="text-[13px]">Buy credits to unlock access to aiRight features.</p>
        </div>
      </Link>
      <div className="my-6 flex flex-col border-y-1 border-gray-100 py-6">
        {listLink.map((link) => (
          <Link
            href={link.link}
            className="mb-5 flex cursor-pointer items-center last:mb-0 hover:opacity-70"
            key={link.key}
            onClick={() => {
              router.push(link.link);
              setActive(false);
            }}
          >
            {linkIcons[link.key]}
            <span className="ml-2 mt-[2px] text-[14px] text-black">{link.text}</span>
          </Link>
        ))}
      </div>
      {auth?.user?.isEmailVerified && (
        <div className="flex flex-col border-b-1 border-gray-100 pb-6">
          <div className="flex items-center rounded-[16px] bg-gray-50 p-3">
            <div className="pr-2">
              <div className="flex rounded-[8px] bg-white px-[10px] py-[8px]">
                <div
                  onClick={(e) => {
                    e.preventDefault();
                    copyClipboard(e, `${BASE_URL}/?refBy=${auth.user.refCode}`);
                  }}
                  className="flex cursor-pointer place-items-end text-[14px]"
                >
                  <span className="text-left">
                    {BASE_URL}/?refBy={auth.user.refCode}
                  </span>
                  <span>
                    <IconCopyRefer />
                  </span>
                </div>
              </div>
            </div>

            <div className="flex h-[80px] w-[80px]">
              <QRCode
                size={80}
                style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
                value={`${BASE_URL}/?refBy=${auth.user.refCode}`}
                viewBox="0 0 80 80"
              />
            </div>
          </div>
        </div>
      )}
      <div
        onClick={() => logout()}
        className="flex cursor-pointer items-center pt-6 hover:opacity-70"
      >
        <IconLogout /> <span className="ml-2 text-[14px]">LOGOUT</span>
      </div>
    </div>
  );

  return (
    <PopoverCustom
      isOpen={active}
      trigger={triggerElement}
      content={contentElement}
      styles={{ top: 'calc(100% + 10px)' }}
      onOpenChange={(isOpen) => {
        setActive(isOpen);
      }}
    />
  );
};

export default UserInfo;
